import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, Timer, Clock, Search, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_BADGE = {
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  IN_SECTION: 'bg-amber-100 text-amber-800',
  UNRESOLVED: 'bg-stone-100 text-stone-600',
};

const SummaryCards = ({ summary }) => {
  if (!summary) return null;
  const cards = [
    { label: 'Completed', value: summary.completed ?? 0, color: 'text-indigo-600' },
    { label: 'Avg Dwell', value: summary.avg_dwell || '—', color: 'text-emerald-600' },
    { label: 'Median', value: summary.median_dwell || '—', color: 'text-teal-600' },
    { label: 'Min', value: summary.min_dwell || '—', color: 'text-stone-700' },
    { label: 'Max', value: summary.max_dwell || '—', color: 'text-rose-600' },
    { label: 'In Section', value: summary.in_section ?? (summary.unresolved ?? 0), color: 'text-amber-600' },
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="dwell-summary-cards">
      {cards.map((c) => (
        <Card key={c.label} className="border-stone-200">
          <CardContent className="p-3">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 truncate">{c.label}</div>
            <div className={`text-xl font-semibold ${c.color}`}>{c.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

function DwellPanel({ kind }) {
  // kind: 'section' | 'dispatch'
  const isSection = kind === 'section';
  const base = isSection ? '/reports/section-dwell' : '/reports/dispatch-dwell';
  const idField = isSection ? 'section' : 'operation';

  const [options, setOptions] = useState([]);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState('__overview__');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const url = isSection ? `${base}/sections` : `${base}/operations`;
    apiClient.get(url).then((r) => {
      setOptions(isSection ? (r.data.sections || []) : (r.data.operations || []));
      setPending(r.data.pending || []);
    }).catch(() => toast.error('Failed to load options'));
  }, [base, isSection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.append('date_from', dateFrom);
      if (dateTo) p.append('date_to', dateTo);
      if (selected && selected !== '__overview__') {
        p.append(idField, selected);
        if (search) p.append('search', search);
        const res = await apiClient.get(`${base}?${p.toString()}`);
        setRows(res.data.rows || []);
        setSummary(res.data.summary || null);
        setSummaries([]);
      } else {
        const res = await apiClient.get(`${base}?${p.toString()}`);
        setSummaries(res.data.summaries || []);
        setRows([]);
        setSummary(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [base, idField, selected, dateFrom, dateTo, search]);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selected, dateFrom, dateTo]);

  const handleExport = () => {
    if (selected === '__overview__') { toast.error('Pick a specific item to export'); return; }
    const p = new URLSearchParams({ [idField]: selected });
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    p.append('site_id', localStorage.getItem('active_site_id') || 'salem');
    const tok = localStorage.getItem('session_token');
    if (tok) p.append('token', tok);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    const a = document.createElement('a');
    a.href = `${apiBase}${base}/export?${p.toString()}`;
    a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('CSV download started');
  };

  const labelOf = (o) => (typeof o === 'string' ? o : o.label);
  const idOf = (o) => (typeof o === 'string' ? o : o.id);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-stone-600">{isSection ? 'Section' : 'Operation'}</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="dwell-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__overview__">Overview (all)</SelectItem>
                {options.map((o) => <SelectItem key={idOf(o)} value={idOf(o)}>{labelOf(o)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-stone-600">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="dwell-date-from" />
          </div>
          <div>
            <label className="text-xs text-stone-600">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="dwell-date-to" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-stone-600">Search (key / sku / reference)</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-2 top-2.5 text-stone-400" />
                <Input className="pl-7" placeholder="Type to filter…" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                  disabled={selected === '__overview__'} data-testid="dwell-search" />
              </div>
              <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700" data-testid="dwell-apply">Apply</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {pending.length > 0 && (
        <div className="text-xs text-stone-500">Pending sections (not yet configured): {pending.join(', ')}</div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="dwell-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        {selected !== '__overview__' && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleExport} data-testid="dwell-export">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        )}
      </div>

      {summary && <SummaryCards summary={summary} />}

      {/* Overview table */}
      {selected === '__overview__' ? (
        <Card><CardContent className="p-0">
          {loading ? <div className="p-10 text-center text-stone-500">Loading…</div> : (
            <Table data-testid="dwell-overview-table">
              <TableHeader><TableRow>
                <TableHead>{isSection ? 'Section' : 'Operation'}</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Avg Dwell</TableHead>
                <TableHead className="text-right">Median</TableHead>
                <TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Max</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {summaries.map((s) => (
                  <TableRow key={s.section_id || s.op_id}
                    className="cursor-pointer hover:bg-stone-50"
                    onClick={() => setSelected(s.section_id || s.op_id)}
                    data-testid={`dwell-ov-row-${s.section_id || s.op_id}`}>
                    <TableCell className="font-medium">{s.section || s.operation}</TableCell>
                    <TableCell className="text-right">{s.completed}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{s.avg_dwell || '—'}</TableCell>
                    <TableCell className="text-right">{s.median_dwell || '—'}</TableCell>
                    <TableCell className="text-right">{s.min_dwell || '—'}</TableCell>
                    <TableCell className="text-right">{s.max_dwell || '—'}</TableCell>
                  </TableRow>
                ))}
                {summaries.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={6} className="text-center p-10 text-stone-400">No data in range.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          {loading ? <div className="p-10 text-center text-stone-500">Loading…</div> : rows.length === 0 ? (
            <div className="p-10 text-center text-stone-400" data-testid="dwell-empty">No records in this range.</div>
          ) : (
            <Table data-testid="dwell-detail-table">
              <TableHeader><TableRow>
                <TableHead>#</TableHead>
                {isSection ? <>
                  <TableHead>Bundle</TableHead><TableHead>USN</TableHead>
                  <TableHead>Design</TableHead><TableHead>SKU</TableHead>
                  <TableHead>Received</TableHead><TableHead>Out / Exit</TableHead>
                  <TableHead>Dest / Method</TableHead><TableHead className="text-right">USNs</TableHead>
                </> : <>
                  <TableHead>Key</TableHead><TableHead>Reference</TableHead>
                  <TableHead>Start</TableHead><TableHead>End</TableHead>
                </>}
                <TableHead className="text-right">Dwell</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`dwell-row-${i}`}>
                    <TableCell className="text-xs text-stone-400">{i + 1}</TableCell>
                    {isSection ? <>
                      <TableCell className="font-mono text-xs">{r.bundle_code || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.usn_code || '—'}</TableCell>
                      <TableCell className="text-xs">{r.design_name || '—'}</TableCell>
                      <TableCell className="text-xs">{r.sku_code || '—'}</TableCell>
                      <TableCell className="text-xs">{(r.received_at || '').slice(0, 10) || '—'}</TableCell>
                      <TableCell className="text-xs">{(r.end_at || '').slice(0, 10) || '—'}</TableCell>
                      <TableCell className="text-xs">{r.end_event || '—'}</TableCell>
                      <TableCell className="text-right text-xs">{r.usn_count ?? '—'}</TableCell>
                    </> : <>
                      <TableCell className="font-mono text-xs">{r.key}</TableCell>
                      <TableCell className="text-xs">{r.reference || '—'}</TableCell>
                      <TableCell className="text-xs">{(r.start_at || '').slice(0, 16).replace('T', ' ') || '—'}</TableCell>
                      <TableCell className="text-xs">{(r.end_at || '').slice(0, 16).replace('T', ' ') || '—'}</TableCell>
                    </>}
                    <TableCell className="text-right font-semibold text-indigo-700">{r.dwell || '—'}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_BADGE[r.status] || 'bg-stone-100'} border-0 text-[10px]`}>{r.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

export default function DwellTimeReport() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-stone-50 p-6" data-testid="dwell-report-root">
      <div className="max-w-[1400px] mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="dwell-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Timer className="h-6 w-6 text-indigo-600" /> Dwell Time Report
            </h1>
            <p className="text-sm text-stone-500">Time taken between operations — per section & per dispatch operation</p>
          </div>
        </div>

        <Tabs defaultValue="section">
          <TabsList data-testid="dwell-tabs">
            <TabsTrigger value="section" data-testid="dwell-tab-section">
              <Clock className="h-4 w-4 mr-1" /> Section Dwell (Receipt → Transfer)
            </TabsTrigger>
            <TabsTrigger value="dispatch" data-testid="dwell-tab-dispatch">
              <PackageCheck className="h-4 w-4 mr-1" /> Dispatch Operations
            </TabsTrigger>
          </TabsList>
          <TabsContent value="section" className="mt-4"><DwellPanel kind="section" /></TabsContent>
          <TabsContent value="dispatch" className="mt-4"><DwellPanel kind="dispatch" /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
